'use client'

/*
 * Live film grain. Ported from the prototype (SOURCE-BACKGROUND-AND-NAV.md §1.6):
 * N 128×128 noise tiles generated once as PNG data-URLs, cycled by a single
 * interval. No per-frame canvas work. The DOM node's background-image is swapped
 * directly through a ref, so cycling frames does not re-render React.
 */

import React, { useEffect, useRef, useState } from 'react'

import styles from './background.module.css'

interface GrainProps {
  /** 0–1 */
  opacity: number
  /** tile count — 8 in the prototype */
  frames: number
  fps: number
  /** dark → overlay, light → multiply (prototype --blend) */
  blend: 'overlay' | 'multiply'
  reducedMotion: boolean
  /** mobile lighter path: halve the cycle rate to save power */
  mobile: boolean
}

const TILE = 128

export function Grain({ opacity, frames, fps, blend, reducedMotion, mobile }: GrainProps) {
  const elRef = useRef<HTMLDivElement>(null)
  const sheetsRef = useRef<string[]>([])
  const [ready, setReady] = useState(false)

  // Generate the tiles once (client only — uses canvas + Math.random).
  useEffect(() => {
    const count = Math.max(1, Math.round(frames))
    const out: string[] = []
    for (let f = 0; f < count; f++) {
      const c = document.createElement('canvas')
      c.width = c.height = TILE
      const x = c.getContext('2d')
      if (!x) continue
      const im = x.createImageData(TILE, TILE)
      const d = im.data
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0
        d[i] = d[i + 1] = d[i + 2] = v
        d[i + 3] = 255
      }
      x.putImageData(im, 0, 0)
      out.push(`url(${c.toDataURL('image/png')})`)
    }
    sheetsRef.current = out
    if (elRef.current && out[0]) elRef.current.style.backgroundImage = out[0]
    setReady(out.length > 0)
  }, [frames])

  // Cycle frames. Reduced motion → stay on frame 0 (static grain, no interval).
  useEffect(() => {
    if (!ready || reducedMotion) return
    const sheets = sheetsRef.current
    if (sheets.length < 2) return
    const rate = mobile ? Math.max(1, Math.round(fps / 2)) : fps
    let gi = 0
    const id = setInterval(() => {
      gi = (gi + 1) % sheets.length
      if (elRef.current) elRef.current.style.backgroundImage = sheets[gi]
    }, 1000 / rate)
    return () => clearInterval(id)
  }, [ready, fps, reducedMotion, mobile])

  return (
    <div
      ref={elRef}
      className={styles.noise}
      style={{ opacity, mixBlendMode: blend }}
    />
  )
}

export default Grain
